import {
    type AnyConnectionType,
    type EdgeForConnection,
    type PlaceholderEdge,
    type PlaceholderNode,
    type NodeForConnection,
    getEdgesWithPlaceholders,
    getNodesWithPlaceholders,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import { type UseConnectionResult, useConnection } from './useConnection';
import {
    type MaybeRefOrGetter,
    toValue,
    type ComputedRef,
    computed,
} from 'vue';

export interface UseConnectionWithPlaceholdersResult<
    C extends AnyConnectionType
> extends UseConnectionResult<C> {
    /**
     * An optimistic guess of the total amount of edges in the connection.
     */
    optimisticTotalCount: ComputedRef<number>;
    /**
     * The amount of placeholders in the result.
     */
    pendingCount: ComputedRef<number>;
    /**
     * Array with both the loaded edges and placeholder-edges.
     */
    edgesWithPlaceholders: ComputedRef<
        (EdgeForConnection<C> | PlaceholderEdge)[]
    >;
    /**
     * Array with both the loaded nodes and placeholder-nodes.
     */
    nodesWithPlaceholders: ComputedRef<
        (NodeForConnection<C> | PlaceholderNode)[]
    >;
}

export interface UseConnectionWithPlaceholdersOptions {
    /**
     * The expected total amount of items. (Needed to get the placeholders before the connection is loaded.)
     */
    expectedTotalCount?: MaybeRefOrGetter<number | null | undefined>;
    /**
     * Some value to influence the Random Number Generators of the placeholder nodes.
     */
    rngSalt?: MaybeRefOrGetter<unknown>;
}

/**
 * Destructs a connection object and exposes computed properties that have placeholder values.
 */
export function useConnectionWithPlaceholders<C extends AnyConnectionType>(
    connection: MaybeRefOrGetter<C | null | undefined>,
    options: UseConnectionWithPlaceholdersOptions = {}
): UseConnectionWithPlaceholdersResult<C> {
    const conn = useConnection(connection);

    const optimisticTotalCount = computed(() => {
        if (conn.totalCount.value !== null) return conn.totalCount.value;
        if (
            conn.hasNextPage.value === false &&
            conn.hasPreviousPage.value === false
        )
            return conn.count.value;
        return toValue(options.expectedTotalCount) ?? conn.count.value;
    });

    const pendingCount = computed(
        () => optimisticTotalCount.value - conn.count.value
    );

    const edgesWithPlaceholders = computed(
        (): (EdgeForConnection<C> | PlaceholderEdge)[] => {
            if (
                conn.hasNextPage.value === false &&
                conn.hasPreviousPage.value === false
            )
                return conn.edges.value;

            return getEdgesWithPlaceholders(
                conn.edges.value,
                optimisticTotalCount.value,
                !!conn.hasPreviousPage.value,
                toValue(options.rngSalt)
            );
        }
    );

    const nodesWithPlaceholders = computed(
        (): (NodeForConnection<C> | PlaceholderNode)[] => {
            if (
                conn.hasNextPage.value === false &&
                conn.hasPreviousPage.value === false
            )
                return conn.nodes.value;

            return getNodesWithPlaceholders(
                conn.nodes.value,
                optimisticTotalCount.value,
                !!conn.hasPreviousPage.value,
                toValue(options.rngSalt)
            );
        }
    );

    return {
        ...conn,
        optimisticTotalCount,
        pendingCount,
        edgesWithPlaceholders,
        nodesWithPlaceholders,
    };
}
